import React, {useCallback, useEffect, useRef} from 'react';
import cx from 'classnames';
import usePortal from 'react-useportal';
import {DatePicker, DropdownItem, noop, Scrollbar} from '@priority-software/priority-style-react';
import {getFnsLocale, getLang, isRTL} from '../../../../../../../../Localization';

import {
    formatDate,
    formatStringToDate,
    isDateTime,
    isMaxLengthReached
} from '../../../../../../../Form/utils/date';

import {apiLocker, formsStore} from '../../../../../../../../stores/Form';
import {keys} from '../../../../../../../../constants/KeyCodes';
import compStyles from './styles.module.scss';
import {texts} from '../../../../../../texts';
import {detectClickOutside} from '../../../../../../../../utils/clickOutsideDefaultHandler';

type Props = {
    isOpen?: boolean;
    portalEl?: HTMLElement;
    value?: string;
    columnType: number;
    getAnchor: () => HTMLElement | null;
    setIsOpen: (flag: boolean) => void;
    onChange?: (val: string) => void;
    boundingRect?: DOMRect;
    options?: {label: string; value: string}[];
    zIndex?: number;
    shiftPositionX?: number;
    shiftPositionY?: number;
    hasCustomDate?: boolean;
};

export const DatePickerExtended = (props: Props) => {
    const {
        portalEl,
        value,
        isOpen,
        setIsOpen,
        columnType,
        onChange,
        boundingRect,
        getAnchor,
        options = [],
        hasCustomDate,
        shiftPositionY = 0,
        shiftPositionX = 0,
        zIndex = 10020
    } = props;

    const {Portal} = usePortal({bindTo: portalEl});
    const datePickerActive = value ? hasCustomDate : true;
    const compRef = useRef<HTMLDivElement>();
    const datepickerRef = useRef();

    const dateStringToStartDate = () => {
        if (value && isMaxLengthReached(value, columnType, getLang())) {
            return formatStringToDate(value, isDateTime(columnType), getLang());
        }
        return null;
    };

    const onDateSelected = (date: Date) => {
        onChange(formatDate(date, columnType));
        setIsOpen(false);
    };

    const onDropdownItemSelect = useCallback(
        (item: {label: string; value: string | number | boolean}) => {
            onChange(item.value as string);
            setIsOpen(false);
        },
        [setIsOpen, onChange]
    );

    const showTime = isDateTime(columnType);
    const pickerIsShown = isOpen && boundingRect;

    useEffect(() => {
        // attach keydown event listener to the content of the calendar
        const wrapEl = compRef?.current;

        if (wrapEl) {
            const handleKeyDown = (e: KeyboardEvent) => {
                if (e.key === keys.ESC) {
                    e.stopPropagation();
                    e.preventDefault();

                    setIsOpen(false);
                }
            };

            window.addEventListener('keydown', handleKeyDown, true);

            // block navigation
            const session = formsStore.currentSession;
            apiLocker.lockSession(session, {navigationOnly: true});

            // focus the selected day to enable navigation in calendar
            const firstDayElement: HTMLInputElement =
                wrapEl.querySelector(`[class*=day--selected]`);

            if (firstDayElement) {
                firstDayElement.focus();
            }

            return () => {
                apiLocker.unlockSession(session);
                window.removeEventListener('keydown', handleKeyDown, true);

                // focus and select input
                const anchor = getAnchor();

                if (anchor instanceof HTMLInputElement) {
                    anchor?.focus();
                    anchor?.select();
                }
            };
        }
        return noop;
    }, [pickerIsShown, getAnchor]);

    useEffect(() => {
        if (isOpen) {
            const handleClickOutside = event => {
                const isClickOutside = detectClickOutside(
                    compRef.current,
                    event.target,
                    'react-onclickoutside-ignore'
                );
                if (isClickOutside) {
                    setIsOpen(false);
                }
            };

            document.addEventListener('click', handleClickOutside, true);

            return () => document.removeEventListener('click', handleClickOutside, true);
        }
        return noop;
    }, [setIsOpen, isOpen]);

    const defaultFocusedIndex = options.findIndex(item => item.value === value);

    useEffect(() => {
        if (pickerIsShown) {
            const items = compRef.current?.querySelectorAll('.pui-dropdown-item');
            if (items && defaultFocusedIndex > -1 && items[defaultFocusedIndex]) {
                items[defaultFocusedIndex].scrollIntoView({
                    block: 'nearest',
                    inline: 'nearest'
                });
            }
        }
    }, [defaultFocusedIndex, pickerIsShown]);

    const getPosition = () => {
        if (boundingRect) {
            const datepickerH = 300;
            const xPosition = isRTL()
                ? {right: window.innerWidth - boundingRect.right + shiftPositionX}
                : {left: boundingRect.left - shiftPositionX};

            if (boundingRect.bottom + shiftPositionY + datepickerH <= window.innerHeight) {
                return {
                    top: boundingRect.bottom + shiftPositionY,
                    ...xPosition
                };
            } else {
                return {
                    top: boundingRect.top - shiftPositionY - datepickerH,
                    ...xPosition
                };
            }
        }
        return {};
    };

    return (
        pickerIsShown && (
            <Portal>
                <div
                    ref={compRef}
                    className={compStyles.component}
                    style={{
                        position: 'fixed',
                        zIndex,
                        ...getPosition()
                    }}
                >
                    <div className={compStyles.sidePart}>
                        <div>
                            <Scrollbar
                                rtl={isRTL()}
                                autoHeight
                                autoHeightMax={220}
                                shiftVerticalScrollPositionX={12}
                                autoHide={false}
                            >
                                {options.map(item => {
                                    return (
                                        <DropdownItem
                                            skin="primary"
                                            onSelect={onDropdownItemSelect}
                                            data={item}
                                            key={item.value}
                                            itemBorder={false}
                                            isSelected={value === item.value}
                                            isFocused={false}
                                        />
                                    );
                                })}
                            </Scrollbar>
                        </div>

                        <DropdownItem
                            skin="primary"
                            onSelect={() => onChange('')}
                            data={{label: texts.date_control_custom_label, value: 'custom'}}
                            itemBorder={false}
                            isSelected={datePickerActive || hasCustomDate}
                        />
                    </div>

                    <div
                        className={cx(compStyles.calendarPart, {
                            [compStyles.inactivePicker]: !(datePickerActive || hasCustomDate)
                        })}
                    >
                        <DatePicker
                            datepickerRef={datepickerRef}
                            locale={getFnsLocale(getLang())}
                            selected={dateStringToStartDate()}
                            onSelect={onDateSelected}
                            onChange={showTime ? onDateSelected : undefined}
                            inline
                            showTimeSelect={showTime}
                            timeIntervals={15}
                            timeCaption=""
                        />
                    </div>
                </div>
            </Portal>
        )
    );
};
